// ═══════════════════════════════════════════════════════════════
// LmsCourseEditor — cockpit d'authoring premium d'un cours
// Structure (modules/leçons) · bases d'expertise · évaluations · publication
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, Globe, EyeOff, Plus, Pencil, Trash2, ChevronUp, ChevronDown,
    Database, ClipboardCheck, Layers, FileText, Video, File as FileIcon,
    MessageSquare, Check, X, BookOpen, Users, Crown,
} from 'lucide-react';
import { lmsApi } from '../../services/lmsApi';
import { toast, confirm as confirmModal } from '../ui/Toast';
import { s, ACCENT, Spinner } from './lmsUi';
import LmsCourseFormModal from './LmsCourseFormModal';
import LmsLessonModal from './LmsLessonModal';
import LmsAssessmentBuilder from './LmsAssessmentBuilder';

const TYPE_META = {
    text: { icon: FileText, label: 'Texte', color: '#8b5cf6' },
    video: { icon: Video, label: 'Vidéo', color: '#8b5cf6' },
    file: { icon: FileIcon, label: 'Fichier', color: '#0ea5e9' },
    chat_task: { icon: MessageSquare, label: 'Tuteur IA', color: '#22c55e' },
    assessment: { icon: ClipboardCheck, label: 'Évaluation', color: '#f59e0b' },
    sandbox: { icon: Layers, label: 'Sandbox', color: '#64748b' },
};

const move = (arr, from, to) => {
    const a = [...arr];
    const [it] = a.splice(from, 1);
    a.splice(to, 0, it);
    return a;
};

export default function LmsCourseEditor({ courseId, user, onBack }) {
    const [course, setCourse] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);

    // Modals
    const [editCourse, setEditCourse] = useState(false);
    const [lessonModal, setLessonModal] = useState(null);      // { moduleId, moduleTitle, lesson? }
    const [assessmentModal, setAssessmentModal] = useState(null); // { assessment? } | true

    const load = useCallback(async () => {
        try {
            const { courses } = await lmsApi.myCourses();
            const c = courses.find((x) => String(x.id) === String(courseId));
            if (!c) { setErr('Cours introuvable'); return; }
            const [deep, asmt] = await Promise.all([
                lmsApi.getCourseBySlug(c.slug),
                lmsApi.listAssessments(c.id).catch(() => ({ assessments: [] })),
            ]);
            setCourse(deep.course);
            setAssessments(asmt.assessments || []);
        } catch (e) { setErr(e.message); }
    }, [courseId]);
    useEffect(() => { load(); }, [load]);

    if (err) return <div style={pad}>⚠ {err} <button onClick={onBack} style={linkBtn}>Retour</button></div>;
    if (!course) return <div style={{ ...pad, color: 'var(--text-secondary)' }}><Spinner /> Chargement…</div>;

    const togglePublish = async () => {
        setBusy(true);
        try {
            const { course: c } = await lmsApi.publishCourse(course.id, !course.is_published);
            setCourse((s2) => ({ ...s2, is_published: c.is_published }));
            toast.success(c.is_published ? 'Cours publié' : 'Repassé en brouillon');
        } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };

    const removeCourse = async () => {
        const ok = await confirmModal(`Archiver le cours « ${course.title} » ? Il ne sera plus visible au catalogue.`, { title: 'Archiver le cours', confirmText: 'Archiver', danger: true });
        if (!ok) return;
        try { await lmsApi.deleteCourse(course.id); toast.success('Cours archivé'); onBack(); }
        catch (e) { toast.error(e.message); }
    };

    const addModule = async () => {
        const c = course;
        try { await lmsApi.addModule(c.id, { title: 'Nouveau module' }); await load(); }
        catch (e) { toast.error(e.message); }
    };

    const reorderModules = async (from, to) => {
        if (to < 0 || to >= course.modules.length) return;
        const reordered = move(course.modules, from, to);
        setCourse((s2) => ({ ...s2, modules: reordered }));
        try { await lmsApi.reorderModules(course.id, reordered.map((m) => m.id)); }
        catch (e) { toast.error(e.message); load(); }
    };

    return (
        <div style={{ ...pad, overflowY: 'auto', height: '100%' }}>
            {/* Header */}
            <button onClick={onBack} style={linkBtn}><ArrowLeft size={14} /> Mes cours</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginTop: 12 }}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 style={{ margin: 0, fontSize: 22 }}>{course.title}</h2>
                        <span style={statusBadge(course.is_published)}>{course.is_published ? 'Publié' : 'Brouillon'}</span>
                    </div>
                    {course.subtitle && <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '6px 0 0' }}>{course.subtitle}</p>}
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => setEditCourse(true)} style={s.ghostBtn}><Pencil size={14} /> Modifier</button>
                    <button onClick={togglePublish} disabled={busy} style={{ ...s.primaryBtn, background: course.is_published ? '#64748b' : ACCENT }}>
                        {course.is_published ? <><EyeOff size={14} /> Dépublier</> : <><Globe size={14} /> Publier</>}
                    </button>
                </div>
            </div>

            {/* Métadonnées */}
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', margin: '14px 0 4px', fontSize: 13, color: 'var(--text-secondary)' }}>
                <Meta label="Prix" value={course.price_eur ? `${course.price_eur} €` : 'Gratuit'} />
                <Meta label="Niveau" value={course.level || '—'} />
                {course.duration_hours ? <Meta label="Durée" value={`${course.duration_hours} h`} /> : null}
                <Meta label="Tuteur / appr." value={course.max_tutor_messages != null ? `${course.max_tutor_messages} msg` : 'illimité'} />
                <Meta label="Projet backing" value={`#${course.project_id}`} />
            </div>

            {/* Bases d'expertise */}
            <Section icon={Database} title="Bases d'expertise du tuteur" subtitle="Les connaissances qui nourrissent le tuteur IA de ce cours.">
                <BasePicker course={course} onChange={load} />
            </Section>

            {/* Formateurs (gestion partagée) */}
            <Section icon={Users} title="Formateurs" subtitle="Le propriétaire peut inviter des co-formateurs qui éditent le cours avec lui.">
                <InstructorsPanel course={course} user={user} onChange={load} />
            </Section>

            {/* Structure */}
            <Section
                icon={BookOpen} title="Structure du cours" subtitle="Modules et leçons, dans l'ordre de progression."
                action={<button onClick={addModule} style={s.ghostBtn}><Plus size={14} /> Module</button>}
            >
                {(course.modules || []).length === 0 ? (
                    <Empty text="Aucun module. Commence par en ajouter un." />
                ) : course.modules.map((m, mi) => (
                    <ModuleRow
                        key={m.id} module={m} index={mi} total={course.modules.length}
                        onReload={load} onReorder={reorderModules}
                        onAddLesson={() => setLessonModal({ moduleId: m.id, moduleTitle: m.title })}
                        onEditLesson={(lesson) => setLessonModal({ moduleId: m.id, moduleTitle: m.title, lesson })}
                    />
                ))}
            </Section>

            {/* Évaluations */}
            <Section
                icon={ClipboardCheck} title="Évaluations" subtitle="QCM auto-notés ou copies notées par l'IA (spectral)."
                action={<button onClick={() => setAssessmentModal({})} style={s.ghostBtn}><Plus size={14} /> Évaluation</button>}
            >
                {assessments.length === 0 ? (
                    <Empty text="Aucune évaluation. Crée-en une pour valider les acquis." />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {assessments.map((a) => (
                            <div key={a.id} style={{ ...s.card, padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</div>
                                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                                        {a.use_spectral ? 'Spectral · réponses ouvertes' : 'QCM'} · {(a.questions || []).length} question(s) · seuil {a.pass_threshold}% · {a.max_attempts != null ? `${a.max_attempts} essais` : 'essais illimités'}
                                    </div>
                                </div>
                                <button onClick={() => setAssessmentModal({ assessment: a })} style={{ ...s.iconBtn }} title="Modifier"><Pencil size={14} /></button>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* Zone danger */}
            <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid var(--card-border)' }}>
                <button onClick={removeCourse} style={{ ...s.ghostBtn, color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}>
                    <Trash2 size={14} /> Archiver ce cours
                </button>
            </div>

            {/* Modals */}
            {editCourse && (
                <LmsCourseFormModal course={course} onClose={() => setEditCourse(false)}
                    onSaved={(c) => { setEditCourse(false); setCourse((s2) => ({ ...s2, ...c })); }} />
            )}
            {lessonModal && (
                <LmsLessonModal {...lessonModal} assessments={assessments} projectId={course.project_id}
                    onClose={() => setLessonModal(null)}
                    onSaved={() => { setLessonModal(null); load(); }} />
            )}
            {assessmentModal && (
                <LmsAssessmentBuilder courseId={course.id} assessment={assessmentModal.assessment}
                    onClose={() => setAssessmentModal(null)}
                    onSaved={() => { setAssessmentModal(null); load(); }} />
            )}
        </div>
    );
}

// ─── Bases d'expertise (sélecteur + chips) ───────────────────────
function InstructorsPanel({ course, user, onChange }) {
    const isOwner = user?.role === 'admin_general' || String(course.instructor_id) === String(user?.id);
    const [ref, setRef] = useState('');
    const [busy, setBusy] = useState(false);
    const instructors = course.instructors || [];

    const add = async () => {
        const v = ref.trim();
        if (!v) return;
        setBusy(true);
        try { await lmsApi.addCourseInstructor(course.id, v); setRef(''); toast.success('Co-formateur ajouté'); onChange(); }
        catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };
    const remove = async (i) => {
        const ok = await confirmModal(`Retirer ${i.full_name || i.username} de la co-formation de ce cours ?`, { confirmText: 'Retirer', danger: true });
        if (!ok) return;
        try { await lmsApi.removeCourseInstructor(course.id, i.user_id); toast.success('Co-formateur retiré'); onChange(); }
        catch (e) { toast.error(e.message); }
    };

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: isOwner ? 12 : 0 }}>
                {instructors.map((i) => {
                    const owner = i.role === 'owner';
                    return (
                        <span key={i.user_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 20, fontSize: 12.5,
                            background: owner ? 'rgba(245,158,11,0.10)' : 'rgba(139,92,246,0.10)',
                            border: `1px solid ${owner ? 'rgba(245,158,11,0.30)' : 'rgba(139,92,246,0.25)'}`,
                            color: owner ? '#d97706' : ACCENT }}>
                            {owner ? <Crown size={12} /> : <Users size={12} />}
                            {i.full_name || i.username}
                            {owner && <span style={{ opacity: 0.7 }}>· propriétaire</span>}
                            {isOwner && !owner && (
                                <button onClick={() => remove(i)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: ACCENT, display: 'inline-flex', padding: 0 }} title="Retirer"><X size={13} /></button>
                            )}
                        </span>
                    );
                })}
            </div>
            {isOwner ? (
                <>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input value={ref} onChange={(e) => setRef(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
                            placeholder="Identifiant, nom d'utilisateur ou email du co-formateur"
                            style={{ ...s.input, flex: 1, maxWidth: 420 }} />
                        <button onClick={add} disabled={!ref.trim() || busy} style={{ ...s.primaryBtn, opacity: ref.trim() && !busy ? 1 : 0.5 }}>
                            {busy ? <Spinner /> : <Plus size={14} />} Inviter
                        </button>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                        Le co-formateur doit déjà être habilité formateur (permission « formateur LMS ») pour voir l'Espace formateur.
                    </div>
                </>
            ) : (
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Seul le propriétaire du cours peut gérer les formateurs.</div>
            )}
        </div>
    );
}

function BasePicker({ course, onChange }) {
    const [available, setAvailable] = useState([]);
    const [picked, setPicked] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => { lmsApi.listBases().then(setAvailable).catch(() => setAvailable([])); }, []);

    const attachedIds = new Set((course.bases || []).map((b) => String(b.base_id)));
    const options = available.filter((b) => !attachedIds.has(String(b.id)));

    const attach = async () => {
        if (!picked) return;
        setBusy(true);
        try { await lmsApi.attachBase(course.id, parseInt(picked, 10)); setPicked(''); toast.success('Base attachée'); onChange(); }
        catch (e) { toast.error(e.message); } finally { setBusy(false); }
    };
    const detach = async (baseId, name) => {
        const ok = await confirmModal(`Détacher la base « ${name} » du tuteur ?`, { confirmText: 'Détacher', danger: true });
        if (!ok) return;
        try { await lmsApi.detachBase(course.id, baseId); toast.success('Base détachée'); onChange(); }
        catch (e) { toast.error(e.message); }
    };

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: (course.bases || []).length ? 12 : 0 }}>
                {(course.bases || []).map((b) => (
                    <span key={b.base_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 20, fontSize: 12.5, background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.25)', color: ACCENT }}>
                        <Database size={12} /> {b.name || b.slug}
                        <button onClick={() => detach(b.base_id, b.name || b.slug)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: ACCENT, display: 'inline-flex', padding: 0 }} title="Détacher"><X size={13} /></button>
                    </span>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
                <select value={picked} onChange={(e) => setPicked(e.target.value)} style={{ ...s.input, flex: 1, cursor: 'pointer', maxWidth: 380 }}>
                    <option value="">{options.length ? '— Choisir une base à attacher —' : 'Aucune base disponible'}</option>
                    {options.map((b) => <option key={b.id} value={b.id}>{b.name || b.slug}</option>)}
                </select>
                <button onClick={attach} disabled={!picked || busy} style={{ ...s.primaryBtn, opacity: picked && !busy ? 1 : 0.5 }}>
                    {busy ? <Spinner /> : <Plus size={14} />} Attacher
                </button>
            </div>
        </div>
    );
}

// ─── Module (rename inline, reorder, delete, leçons) ─────────────
function ModuleRow({ module, index, total, onReload, onReorder, onAddLesson, onEditLesson }) {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(module.title);
    const lessons = module.lessons || [];

    const saveTitle = async () => {
        const t = title.trim();
        setEditing(false);
        if (!t || t === module.title) { setTitle(module.title); return; }
        try { await lmsApi.updateModule(module.id, { title: t }); onReload(); }
        catch (e) { toast.error(e.message); setTitle(module.title); }
    };
    const remove = async () => {
        const ok = await confirmModal(`Supprimer le module « ${module.title} » et ses ${lessons.length} leçon(s) ?`, { confirmText: 'Supprimer', danger: true });
        if (!ok) return;
        try { await lmsApi.deleteModule(module.id); toast.success('Module supprimé'); onReload(); }
        catch (e) { toast.error(e.message); }
    };
    const reorderLesson = async (from, to) => {
        if (to < 0 || to >= lessons.length) return;
        const ids = move(lessons, from, to).map((l) => l.id);
        try { await lmsApi.reorderLessons(module.id, ids); onReload(); }
        catch (e) { toast.error(e.message); }
    };
    const removeLesson = async (lesson) => {
        const ok = await confirmModal(`Supprimer la leçon « ${lesson.title} » ?`, { confirmText: 'Supprimer', danger: true });
        if (!ok) return;
        try { await lmsApi.deleteLesson(lesson.id); toast.success('Leçon supprimée'); onReload(); }
        catch (e) { toast.error(e.message); }
    };

    return (
        <div style={{ ...s.card, marginBottom: 12, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: 'var(--bg)', borderBottom: lessons.length ? '1px solid var(--card-border)' : 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <button onClick={() => onReorder(index, index - 1)} disabled={index === 0} style={reorderBtn(index === 0)}><ChevronUp size={14} /></button>
                    <button onClick={() => onReorder(index, index + 1)} disabled={index === total - 1} style={reorderBtn(index === total - 1)}><ChevronDown size={14} /></button>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>M{index + 1}</span>
                {editing ? (
                    <input
                        value={title} autoFocus onChange={(e) => setTitle(e.target.value)}
                        onBlur={saveTitle} onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setEditing(false); setTitle(module.title); } }}
                        style={{ ...s.input, flex: 1, padding: '6px 10px' }}
                    />
                ) : (
                    <strong style={{ flex: 1, fontSize: 14, cursor: 'pointer' }} onClick={() => setEditing(true)}>{module.title}</strong>
                )}
                {editing ? (
                    <button onClick={saveTitle} style={{ ...s.iconBtn, color: '#22c55e' }} title="Valider"><Check size={14} /></button>
                ) : (
                    <button onClick={() => setEditing(true)} style={s.iconBtn} title="Renommer"><Pencil size={13} /></button>
                )}
                <button onClick={onAddLesson} style={s.iconBtn} title="Ajouter une leçon"><Plus size={14} /></button>
                <button onClick={remove} style={{ ...s.iconBtn, color: '#ef4444' }} title="Supprimer le module"><Trash2 size={13} /></button>
            </div>

            {lessons.length === 0 ? (
                <div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Aucune leçon. <button onClick={onAddLesson} style={linkBtn}>Ajouter une leçon</button>
                </div>
            ) : lessons.map((l, li) => {
                const meta = TYPE_META[l.content_type] || TYPE_META.text;
                const Icon = meta.icon;
                return (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: li ? '1px solid var(--card-border)' : 'none' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <button onClick={() => reorderLesson(li, li - 1)} disabled={li === 0} style={reorderBtn(li === 0)}><ChevronUp size={12} /></button>
                            <button onClick={() => reorderLesson(li, li + 1)} disabled={li === lessons.length - 1} style={reorderBtn(li === lessons.length - 1)}><ChevronDown size={12} /></button>
                        </div>
                        <div style={{ width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${meta.color}1a`, color: meta.color, flexShrink: 0 }}>
                            <Icon size={14} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 500 }}>{l.title}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                                {meta.label}{l.duration_minutes ? ` · ${l.duration_minutes} min` : ''}{l.is_preview ? ' · aperçu gratuit' : ''}
                            </div>
                        </div>
                        <button onClick={() => onEditLesson(l)} style={s.iconBtn} title="Modifier"><Pencil size={13} /></button>
                        <button onClick={() => removeLesson(l)} style={{ ...s.iconBtn, color: '#ef4444' }} title="Supprimer"><Trash2 size={13} /></button>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Primitives locales ──────────────────────────────────────────
const Section = ({ icon: Icon, title, subtitle, action, children }) => (
    <div style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(139,92,246,0.10)', border: '1px solid rgba(139,92,246,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={15} style={{ color: ACCENT }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{title}</div>
                {subtitle && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{subtitle}</div>}
            </div>
            {action}
        </div>
        {children}
    </div>
);

const Meta = ({ label, value }) => (
    <span><span style={{ color: 'var(--text-muted)' }}>{label} : </span><strong style={{ color: 'var(--text)' }}>{value}</strong></span>
);

const Empty = ({ text }) => (
    <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, border: '1px dashed var(--card-border)', borderRadius: 12 }}>{text}</div>
);

const pad = { padding: '24px 32px' };
const linkBtn = { background: 'none', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0 };
const statusBadge = (pub) => ({ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: pub ? 'rgba(34,197,94,0.15)' : 'var(--card-border)', color: pub ? '#16a34a' : 'var(--text-secondary)' });
const reorderBtn = (disabled) => ({ border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer', color: disabled ? 'var(--card-border)' : 'var(--text-muted)', padding: 0, lineHeight: 0, display: 'flex' });
